import React, { FC, useEffect, useState } from 'react';
import { IndexBar } from 'antd-mobile';

// Import from new structure
import { ClientJson } from '../../../types/models';
import { apiClient } from '../../../api';
import { faroLogger } from '../../../observability/faro-logger';

const AVATAR_COLORS = [
  '#1a6fa8', '#2a7a5c', '#7a4a2a', '#5a2a7a',
  '#1a5a8a', '#8a2a2a', '#2a5a4a', '#6a3a6a',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) { hash = name.charCodeAt(i) + ((hash << 5) - hash); }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) { return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase(); }
  return name.slice(0, 2).toUpperCase();
}

const ClientItem: FC<{ item: ClientJson; onClick: () => void }> = ({ item, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 16px',
      background: 'var(--bg-card)',
      borderBottom: '1px solid rgba(49,220,200,0.08)',
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
    }}
  >
    <div style={{
      width: '38px',
      height: '38px',
      borderRadius: '50%',
      background: getAvatarColor(item.name),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontSize: '13px',
      fontWeight: 700,
      color: 'var(--text-primary)',
      letterSpacing: '0.05em',
    }}>
      {getInitials(item.name)}
    </div>
    <span style={{
      fontSize: '14px',
      fontWeight: 500,
      color: 'var(--text-primary)',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}>
      {item.name}
    </span>
    <span style={{ fontSize: '16px', color: 'rgba(49,220,200,0.4)' }}>›</span>
  </div>
);

const ClientList: FC<{ onChooseClient: (client: ClientJson) => void; search: string }> = ({
  onChooseClient,
  search,
}) => {
  const [clients, setClients] = useState<ClientJson[]>([]);
  const [recentClients, setRecentClients] = useState<ClientJson[]>([]);

  useEffect(() => {
    let isMounted = true;

    faroLogger.info('client_list_opened', { hasSearch: !!search });

    apiClient.clients
      .getClients()
      .then((response) => {
        if (isMounted) {
          const activeClients = response.filter((c: ClientJson) => !c.archived);
          setClients(activeClients);
          faroLogger.info('clients_loaded', { count: activeClients.length });
        }
      })
      .catch((error) => {
        console.log(error);
        faroLogger.error('clients_load_failed', { error: error?.message || 'Unknown error' });
      });

    apiClient.clients
      .getRecentClients()
      .then((response) => {
        if (isMounted) {
          const recentActive = response.filter((c: ClientJson) => !c.archived);
          setRecentClients(recentActive);
          faroLogger.info('recent_clients_loaded', { count: recentActive.length });
        }
      })
      .catch((error) => {
        console.log(error);
        faroLogger.error('recent_clients_load_failed', { error: error?.message || 'Unknown error' });
      });

    return () => { isMounted = false; };
  }, []);

  const clientsFiltered = clients.filter((c) => c.name.toLowerCase().includes(search?.toLowerCase()));

  const charCodeOfA = 'A'.charCodeAt(0);
  const alphaGroups = Array(26)
    .fill('')
    .map((_, i) => ({
      title: String.fromCharCode(charCodeOfA + i),
      key: String.fromCharCode(charCodeOfA + i),
      items: clientsFiltered.filter((c) => c.name[0] === String.fromCharCode(charCodeOfA + i)),
    }))
    .filter((g) => g.items.length > 0);

  const recentFiltered = recentClients.filter((c) => c.name.toLowerCase().includes(search?.toLowerCase()));
  const groups = [
    { title: 'Récents', items: recentFiltered, key: '#' },
    ...alphaGroups,
  ];

  return (
    <IndexBar
      sticky={true}
      style={{
        '--sticky-offset-top': '0px',
      } as React.CSSProperties}
    >
      {groups.map((group) => {
        const { title, items, key } = group;
        if (items.length === 0) { return null; }
        return (
          <IndexBar.Panel index={key} title={title} key={key}>
            <div style={{ marginBottom: '4px' }}>
              {/* Section header */}
              <div style={{
                padding: '6px 16px',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--teal)',
                background: 'var(--navy)',
                borderTop: key !== '#' ? '1px solid rgba(49,220,200,0.1)' : undefined,
              }}>
                {title}
                <span style={{ color: 'rgba(49,220,200,0.4)', marginLeft: '6px', fontWeight: 400 }}>
                  {items.length}
                </span>
              </div>
              {items.map((item, index) => (
                <ClientItem
                  key={index}
                  item={item}
                  onClick={() => {
                    faroLogger.info('client_selected', {
                      clientId: item.id,
                      clientName: item.name,
                      fromRecent: key === '#',
                    });
                    onChooseClient(item);
                  }}
                />
              ))}
            </div>
          </IndexBar.Panel>
        );
      })}
    </IndexBar>
  );
};

export default ClientList;
